import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import type {SliceComponentProps} from '@prismicio/react'
import cn from 'classnames'
import {DEFAULT_PADDING, PADDING_BOTTOM_CLASS, PADDING_TOP_CLASS} from 'src/common/paddingClasses'

export type Explainer3TilesProps = SliceComponentProps<Content.Explainer3TilesSlice>

const Explainer3Tiles = ({slice}: Explainer3TilesProps): JSX.Element => {
  const p = slice.primary
  const isDark = p.background_color === 'Dark green'

  const bgMap: Record<string, string> = {
    'Dark green': 'bg-green-dark',
    'Grey/100': 'bg-moss-grey-100',
    'Moss White': 'bg-moss-white',
  }

  const bgClass = bgMap[p.background_color ?? 'Dark green'] ?? 'bg-green-dark'
  const ptClass = PADDING_TOP_CLASS[p.padding_top ?? DEFAULT_PADDING] ?? ''
  const pbClass = PADDING_BOTTOM_CLASS[p.padding_bottom ?? DEFAULT_PADDING] ?? ''

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      data-nav-dark={isDark || undefined}
      className={cn(bgClass, ptClass, pbClass, isDark ? 'text-moss-white' : 'text-moss-black')}
    >
      <div className="mx-auto max-w-[1280px] px-5 md:px-10">
        {p.eyebrow && (
          <p
            className={cn(
              'text-[11px] font-semibold uppercase tracking-widest',
              isDark ? 'text-green-neon' : 'text-green-deep',
            )}
          >
            {p.eyebrow}
          </p>
        )}
        {p.heading && (
          <h2 className={cn('text-h3 md:text-h2 mb-5 mt-4', isDark ? 'text-moss-white' : 'text-moss-black')}>
            {p.heading}
          </h2>
        )}
        {p.subtext && (
          <p
            className={cn(
              'mb-14 max-w-[680px] text-[19px] leading-relaxed',
              isDark ? 'text-moss-white/70' : 'text-black/65',
            )}
          >
            {p.subtext}
          </p>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {p.cards.map((card, i) => (
            <article
              key={i}
              className={cn(
                'flex flex-col rounded-lg border p-7.5',
                isDark ? 'border-moss-white/10 bg-moss-white/[0.06]' : 'border-black/[0.08] bg-black/[0.03]',
              )}
            >
              {card.num && (
                <div className={cn('mb-6 text-[13px] font-semibold', isDark ? 'text-green-neon' : 'text-green-deep')}>
                  {card.num}
                </div>
              )}
              <h3 className={cn('text-h3 mb-4', isDark ? 'text-moss-white' : 'text-moss-black')}>{card.title}</h3>
              {card.description && (
                <p className={cn('text-[16px] leading-[1.55]', isDark ? 'text-moss-white/70' : 'text-black/65')}>
                  {card.description}
                </p>
              )}
              {isFilled.image(card.image) && (
                <div className="mt-auto pt-6">
                  <PrismicNextImage
                    field={card.image}
                    className="aspect-square w-full rounded-lg object-cover"
                    sizes="(max-width: 768px) 100vw, 33vw"
                  />
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

export default Explainer3Tiles
